<template>
  <div class="w-100 team-accordion-container" @mouseenter="onMouseEnter">
    <div class="d-flex align-items-baseline team-accordion pointer plus-icon-container">
      <collapse-caret
        class="flex-shrink-0"
        :size="16"
        :expanded="isExpanded"
        :container-to-icon-ratio="0.5"
        @click="isExpanded = !isExpanded"
      />
      <div class="team-name flex-grow-1" @click="onTeamClick">
        {{ team.name }}
      </div>
      <team-settings :team="team" v-if="isUserTeamAdmin">
        <action-icon
          icon="ellipsis-horizontal-icon"
          class="align-self-center action-icon-component flex-shrink-0"
        />
      </team-settings>
      <action-icon
        icon="plus-icon"
        tooltip-text="Add a new Project"
        class="add-project align-self-center action-icon-component mr-1 flex-shrink-0"
        @click="createProjectModalShown = true"
        v-if="isProjectsEnabled && isProjectEnabledInTeam"
      />
    </div>
    <b-collapse class="team-items mw-0" v-model="isExpanded">
      <team-link v-if="isGoalEnabledInTeam" :team="team" label="Goals" :show-icon="true" />
      <div class="mw-0" :key="listKey" ref="listContainer">
        <template
          v-if="
            isProjectsEnabled &&
              isProjectEnabledInTeam &&
              !isFetchingProjects &&
              projects &&
              projects.length > 0
          "
        >
          <project-link
            :show-gripper="true"
            :data-id="project.id"
            :project="project"
            :key="project.id"
            v-for="project in projects"
          />
        </template>
      </div>
    </b-collapse>
    <manage-project
      v-model="createProjectModalShown"
      :team-id="team.id"
      :org-id="team.organizationId"
      v-if="createProjectModalShown"
    />
  </div>
</template>

<script>
import CollapseCaret from '@/components/CollapseCaret.vue'
import ManageProject from '@/components/ManageProject.vue'
import ProjectLink from '@/components/project/ProjectLink.vue'
import ActionIcon from '@/components/ActionIcon.vue'
import TeamLink from '@/components/sidebar/TeamLink.vue'
import TeamSettings from '@/components/team/TeamSettings.vue'
import { featureFlags } from '@/constants/featureFlags'
import { USER_TYPE } from '@/constants/user'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import SidebarMixin from '@/mixins/SidebarMixin'
import { BCollapse } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SidebarTeamAccordion',
  props: ['team'],
  mixins: [SidebarMixin, FeaturesMixin, DragAndDropMixin],
  components: {
    TeamSettings,
    ManageProject,
    ActionIcon,
    ProjectLink,
    TeamLink,
    CollapseCaret,
    BCollapse
  },
  data() {
    return {
      isExpanded: false,
      listKey: 1,
      createProjectModalShown: false,
      isFetchingProjects: false,
      isFetchingMembership: false,
      isProjectsEnabled: !!this.getFeatureFlagValue(
        this.team.organizationId,
        featureFlags.ALLOW_PROJECTS
      )
    }
  },
  computed: {
    ...mapGetters('project', ['projectById']),
    isProjectEnabledInTeam() {
      return this.team.preferences.enableProjects
    },
    isGoalEnabledInTeam() {
      return this.team.preferences.enableGoals
    },
    projects() {
      return this.$store.getters['project/projectsByTeamId'](this.team.id)
    },
    isActive() {
      const projectId = this.$route.params.projectId
      let isTeamActive = this.isActiveTeam(this.team.id, true)
      if (!isTeamActive && projectId) {
        isTeamActive = this.projectById(projectId)?.teamId === this.team.id
      }
      return isTeamActive
    },
    isUserTeamAdmin() {
      const teamMembership = this.$store.getters.teamMemberships(this.team.id)
      const userId = this.$store.getters.user.id
      return (
        teamMembership.find(membership => membership.userId === userId)?.type === USER_TYPE.OWNER
      )
    }
  },
  methods: {
    ...mapActions('project', ['getTeamProjects']),
    ...mapActions(['getTeamMemberships', 'changeRank']),
    async fetchMemberships() {
      this.isFetchingMembership = true
      this.getTeamMemberships({ orgId: this.team.organizationId, teamId: this.team.id }).finally(
        () => (this.isFetchingMembership = false)
      )
    },
    onTeamClick() {
      this.isExpanded = !this.isExpanded
    },
    fetchProjects() {
      this.isFetchingProjects = true
      return this.getTeamProjects({
        orgId: this.team.organizationId,
        teamId: this.team.id
      }).finally(() => {
        this.isFetchingProjects = false
      })
    },
    onMouseEnter() {
      if (!this.team.memberships && !this.isFetchingMembership) {
        this.fetchMemberships()
      }
    },

    async onDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: this.team.organizationId,
          type: 'project',
          subjectId,
          beforeId,
          afterId
        })
      } catch {
        this.listKey++
        this.enableListDragDrop()
      }
    },
    enableListDragDrop() {
      this.$nextTick(() => {
        if (this.projects?.length > 0) {
          this.enableDragAndDrop({
            containers: [this.$refs.listContainer],
            onDrop: this.onDrop,
            isFullyDraggable: true
          })
        }
      })
    }
  },
  mounted() {
    if (this.isActive) {
      this.isExpanded = true
      this.fetchProjects().then(this.enableListDragDrop)
    }
  },
  watch: {
    isExpanded(newValue) {
      if (newValue && !this.projects && !this.isFetchingProjects) {
        this.fetchProjects()
      }
    },
    isActive(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.isExpanded = true
      }
    },
    projects: 'enableListDragDrop'
  }
}
</script>

<style lang="scss" scoped>
.team-accordion {
  padding: 4px 12px 4px 13px;
}

.team-items {
  margin-left: 20px;

  &:empty {
    padding-bottom: 8px;
  }
}

.team-name {
  margin-left: 3px;
}

.team-accordion-container {
  margin-bottom: 3px;
}

.action-icon-component {
  opacity: 0;
}

.plus-icon-container:hover .action-icon-component {
  opacity: 1;
}
</style>
